import { SocketController } from './socket_controller';
import { WebSocketTransport } from './_web_socket_transport';
import { HttpConnection } from './http_connection';
import { Reporter } from './_reporter';
import { EventEmitter } from './_event_emitter';

function noop(): void {
	// Empty
}

/**
 * ## @visiba/axon
 * WebSocket client to handle a real time connection that's mapped to a .NET architecture.
 *
 * The Axon's "createController" method creates an controller that can be
 * listen on once an WebSocket message comes by with an reference to the created
 * Controller.
 *
 * The Axon holds an WebSocket instance as long as it's activated.
 *
 * ### Example code
 *
 * ```ts
 * const axon = new Axon();
 *
 * const controller = axon.createController('controllerName');
 * controller.on('message', (data) => {
 *   console.log(data);
 * });
 *
 * await axon.open('wss://www.visibacare.com:443');
 * ```
 */
export class Axon {
	public readonly events = new EventEmitter<{
		onClose: null;
		onOpen: null;
		onReconnecting: null;
		onReconnected: null;
	}>();
	private readonly webSocketTransport: WebSocketTransport;
	private readonly httpConnection: HttpConnection;
	private readonly reporter: Reporter;

	// The "Function" type should be change when we can distribute around a better logger
	// TODO: Tell Emil to type this better...
	// eslint-disable-next-line @typescript-eslint/ban-types
	constructor(url: string, logger?: Function) {
		this.reporter = new Reporter(logger ?? noop);
		this.webSocketTransport = WebSocketTransport.create(WebSocket);
		this.httpConnection = HttpConnection.create(url, 5 * 1000, this.webSocketTransport, this.reporter);

		this.httpConnection.onGracefulShutdown = () => this.handleGracefulShutDown();
		this.httpConnection.onReconnect = () => this.events.emit('onReconnecting', null);
		this.httpConnection.onReconnected = () => this.events.emit('onReconnected', null);
	}

	public open(): Promise<void> {
		const promise = this.httpConnection.open();
		promise.then(() => {
			this.events.emit('onOpen', null);
		});

		return promise;
	}

	public close(): Promise<void> {
		const promise = this.httpConnection.close();
		promise.then(() => {
			this.events.emit('onClose', null);
		});

		return promise;
	}

	/**
	 * @deprecated Use getController instead.
	 */
	public createController(controllerName: string): SocketController {
		return this.getController(controllerName);
	}

	public getController(controllerName: string): SocketController {
		if (controllerName === 'ping') {
			console.warn('ping is reserved for internal usage');
		}

		return this.httpConnection.controllerContext.get(controllerName);
	}

	/** Appends an parameter. Parameters is used when opening a WebSocket. */
	public setParameters(value: Record<string, unknown>): void {
		this.httpConnection.parameterHandler.append(value);
	}

	/** Clears all parameters. Parameters is used when opening a WebSocket. */
	public clearParameters(): void {
		this.httpConnection.parameterHandler.clearParameters();
	}

	private handleGracefulShutDown(): void {
		this.events.emit('onClose', null);
	}
}
